import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';

type RoleTabKey = 'all' | 'wk' | 'bat' | 'ar' | 'bowl';
const ROLE_TAB_TO_ROLE: Record<Exclude<RoleTabKey, 'all'>, string> = {
  wk: 'WK-Batsman',
  bat: 'Batsman',
  ar: 'All-Rounder',
  bowl: 'Bowler'
};

const ROLE_TO_ROLE_TAB: Record<string, RoleTabKey> = {
  'WK-Batsman': 'wk',
  Batsman: 'bat',
  'All-Rounder': 'ar',
  Bowler: 'bowl'
};

function getRoleIcon(role: string): React.ReactNode {
  switch (role) {
    case 'Batsman':
      return (
        <img
          src="/images/baticon.png"
          alt="Bat"
          className="inline-block w-10 h-10 object-contain"
          style={{ display: 'inline-block', verticalAlign: 'middle' }}
        />
      );
    case 'Bowler':
      return '🥎';
    case 'WK-Batsman':
      return '🧤';
    case 'All-Rounder':
      return '🏏';
    default:
      return '👤';
  }
}

const ROLE_OPTIONS = [
  { value: '', label: 'All roles' },
  { value: 'WK-Batsman', label: 'Wicket-Keeper' },
  { value: 'Batsman', label: 'Batsman' },
  { value: 'All-Rounder', label: 'All-Rounder' },
  { value: 'Bowler', label: 'Bowler' }
];

export type PlayerPickerStagedPick = {
  tournament_player_id: number;
  name: string;
  role?: string;
  credits?: number | string;
  team?: string;
  player_id?: string | number;
};

export interface PlayerPickerModalProps {
  isOpen: boolean;
  onBack: () => void;
  title: string;
  players: any[];
  excludePlayerIds?: (number | string)[];
  /** When set, role filter is hidden and list is filtered by this role */
  fixedRole?: string | null;
  teamOptions?: string[];
  /** id, name, and optionally team1/team2 for filtering players by match */
  matchOptions?: { id: string; name: string; team1?: string; team2?: string }[];
  /** When false, row is disabled and not clickable. Default true. */
  canSelect?: (player: any) => boolean;
  /** Optional subtext per row (e.g. "In Team", credit change) */
  getRowSubtext?: (player: any) => React.ReactNode;
  /** Optional: player_id list for foreign players – show ✈ next to name when in list */
  foreignPlayerIds?: string[];
  /** When true, caller owns body scroll lock (e.g. BudgetLeaguePage); modal will not set body overflow */
  disableBodyScrollLock?: boolean;
  onSelect: (player: any) => void;
  /** Default `tap`: whole row selects. `add_button`: per-row + only; picked list with − when stagedPicks + onRemoveStaged provided. */
  rowAction?: 'tap' | 'add_button';
  /** Current squad in picker (H2H batch mode). Shown above filters with − to remove. */
  stagedPicks?: PlayerPickerStagedPick[];
  /** Remove pick by tournament_player_id. Omit when picks are read-only (e.g. locked). */
  onRemoveStaged?: (tournamentPlayerId: number) => void;
  /** Max squad size for "Picked (n/N)" label; default 11 */
  stagedPicksMax?: number;
  /** Total credits cap (e.g. 100). When provided in add mode, modal shows used/remaining credits. */
  creditsCap?: number;
}

export const PlayerPickerModal: React.FC<PlayerPickerModalProps> = ({
  isOpen,
  onBack,
  title,
  players,
  excludePlayerIds = [],
  fixedRole = null,
  teamOptions = [],
  matchOptions = [],
  canSelect,
  getRowSubtext,
  foreignPlayerIds = [],
  disableBodyScrollLock = false,
  onSelect,
  rowAction = 'tap',
  stagedPicks: stagedPicksProp,
  onRemoveStaged,
  stagedPicksMax = 11,
  creditsCap
}) => {
  const stagedPicks = stagedPicksProp ?? [];
  const isForeign = (p: any) => foreignPlayerIds.length > 0 && foreignPlayerIds.includes(String(p?.player_id ?? ''));
  const ForeignIcon = () => <span className="text-orange-400 text-sm ml-1 inline-flex shrink-0" title="Foreign player" aria-label="Foreign player">✈</span>;
  const isStagedForeign = (p: PlayerPickerStagedPick) =>
    foreignPlayerIds.length > 0 && foreignPlayerIds.includes(String(p?.player_id ?? ''));
  const useAddButton = rowAction === 'add_button';
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [matchFilter, setMatchFilter] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'credits'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [roleTab, setRoleTab] = useState<RoleTabKey>(() => {
    if (fixedRole && ROLE_TO_ROLE_TAB[fixedRole]) return ROLE_TO_ROLE_TAB[fixedRole];
    return 'all';
  });

  useEffect(() => {
    if (fixedRole && ROLE_TO_ROLE_TAB[fixedRole]) {
      setRoleTab(ROLE_TO_ROLE_TAB[fixedRole]);
      return;
    }
    if (!fixedRole) setRoleTab('all');
  }, [fixedRole]);

  const excludeSet = useMemo(() => {
    const set = new Set<number>();
    (excludePlayerIds || []).forEach((id) => set.add(Number(id)));
    return set;
  }, [excludePlayerIds]);

  const stagedIdSet = useMemo(() => {
    const set = new Set<number>();
    stagedPicks.forEach((pick) => set.add(Number(pick.tournament_player_id)));
    return set;
  }, [stagedPicks]);

  const pickedCountsByRole = useMemo(() => {
    const counts: Record<RoleTabKey, number> = { all: stagedPicks.length, wk: 0, bat: 0, ar: 0, bowl: 0 };
    stagedPicks.forEach((pick) => {
      const key = ROLE_TO_ROLE_TAB[pick.role || ''];
      if (key) counts[key] += 1;
    });
    return counts;
  }, [stagedPicks]);

  const pickedCreditsTotal = useMemo(() => {
    return stagedPicks.reduce((sum, pick) => sum + (parseFloat(String(pick.credits ?? 0)) || 0), 0);
  }, [stagedPicks]);

  const creditsRemaining = creditsCap != null ? creditsCap - pickedCreditsTotal : null;

  const effectiveTeamOptions = useMemo(() => {
    if (teamOptions.length > 0) return teamOptions;
    const teams = Array.from(new Set((players || []).map((p: any) => p.team || p.team_name).filter(Boolean)));
    return teams.sort();
  }, [teamOptions, players]);

  const filteredAndSorted = useMemo(() => {
    let list = [...(players || [])];
    list = list.filter((p: any) => {
      const id = Number(p.tournament_player_id ?? p.id);
      if (!useAddButton && excludeSet.has(id)) return false;
      const tabRole = roleTab === 'all' ? '' : ROLE_TAB_TO_ROLE[roleTab];
      const role = fixedRole !== undefined && fixedRole !== null
        ? fixedRole
        : (useAddButton ? tabRole : roleFilter);
      if (role && (p.role || '') !== role) return false;
      if (teamFilter && (p.team || p.team_name || '') !== teamFilter) return false;
      if (matchFilter && matchOptions.length > 0) {
        const match = matchOptions.find((m) => m.id === matchFilter);
        if (match && (match.team1 != null || match.team2 != null)) {
          const team = p.team || p.team_name || '';
          if (!team) return false;
          if (match.team1 !== team && match.team2 !== team) return false;
        }
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const name = (p.name || p.player_name || '').toLowerCase();
        const team = (p.team || p.team_name || '').toLowerCase();
        if (!name.includes(q) && !team.includes(q)) return false;
      }
      return true;
    });
    const mult = sortOrder === 'asc' ? 1 : -1;
    list.sort((a: any, b: any) => {
      if (useAddButton) {
        const aPicked = stagedIdSet.has(Number(a.tournament_player_id ?? a.id));
        const bPicked = stagedIdSet.has(Number(b.tournament_player_id ?? b.id));
        if (aPicked !== bPicked) return aPicked ? -1 : 1;
      }
      if (sortBy === 'name') {
        const na = (a.name || a.player_name || '').toLowerCase();
        const nb = (b.name || b.player_name || '').toLowerCase();
        return mult * na.localeCompare(nb);
      }
      const ca = parseFloat(a.credits) || 0;
      const cb = parseFloat(b.credits) || 0;
      return mult * (ca - cb);
    });
    return list;
  }, [players, excludeSet, fixedRole, useAddButton, roleTab, roleFilter, teamFilter, matchFilter, matchOptions, searchQuery, sortBy, sortOrder, stagedIdSet]);

  useEffect(() => {
    if (!isOpen || disableBodyScrollLock) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen, disableBodyScrollLock]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setRoleFilter('');
    setTeamFilter('');
    setMatchFilter('');
  };

  const toggleSort = (field: 'name' | 'credits') => {
    if (sortBy === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder(field === 'name' ? 'asc' : 'desc');
    }
  };

  if (!isOpen) return null;

  const content = (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4 min-h-[100dvh] overflow-hidden"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overscrollBehavior: 'contain' }}
    >
      <div className="relative rounded-xl border border-slate-600 bg-slate-800 shadow-2xl w-full max-w-4xl flex flex-col h-[85dvh] max-h-[90vh] overflow-hidden">
        <div className="p-3 sm:p-6 flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={onBack}
              className="text-slate-300 hover:text-white mr-3"
            >
              ← Back
            </button>
            <h2 className="text-xl font-bold text-white text-center flex-1">{title}</h2>
            <span className="w-16" aria-hidden />
          </div>

          {useAddButton && !fixedRole && (
            <div className="mb-3 shrink-0">
              {creditsCap != null && (
                <div className="mb-2 rounded-lg border border-slate-600 bg-slate-900/50 px-3 py-2 text-xs sm:text-sm text-slate-200 flex items-center justify-between gap-3">
                  <span>
                    Credits: <strong className={pickedCreditsTotal <= creditsCap ? 'text-emerald-300' : 'text-red-300'}>{pickedCreditsTotal.toFixed(1)}/{creditsCap}</strong>
                  </span>
                  <span>
                    Left: <strong className={(creditsRemaining ?? 0) >= 0 ? 'text-emerald-300' : 'text-red-300'}>{(creditsRemaining ?? 0).toFixed(1)}</strong>
                  </span>
                </div>
              )}
              <div className="grid grid-cols-5 gap-1 sm:gap-2">
                {([
                  { key: 'wk', label: 'WK' },
                  { key: 'bat', label: 'BAT' },
                  { key: 'ar', label: 'AR' },
                  { key: 'bowl', label: 'BOWL' },
                  { key: 'all', label: 'ALL' }
                ] as { key: RoleTabKey; label: string }[]).map((tab) => {
                  const active = roleTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setRoleTab(tab.key)}
                      className={`rounded-lg px-2 py-2 text-[11px] sm:text-xs font-semibold border transition-colors ${
                        active
                          ? 'bg-blue-600 border-blue-500 text-white'
                          : 'bg-slate-700/70 border-slate-600 text-slate-200 hover:bg-slate-600'
                      }`}
                    >
                      {tab.label} ({pickedCountsByRole[tab.key]})
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mb-3 sm:mb-4 space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Search players..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={handleClearFilters}
                className="p-2 rounded-lg bg-slate-600 hover:bg-slate-500 text-slate-200 border border-slate-500"
                title="Clear all filters"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {!useAddButton && (fixedRole === undefined || fixedRole === null) && (
              <div>
                <label className="block text-white text-xs font-medium mb-1">Role</label>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
                >
                  {ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value} className="bg-slate-700 text-white">
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <select
                value={teamFilter}
                onChange={(e) => {
                  setTeamFilter(e.target.value);
                  setMatchFilter('');
                }}
                className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="" className="bg-slate-700 text-white">All Teams</option>
                {effectiveTeamOptions.map((team) => (
                  <option key={team} value={team} className="bg-slate-700 text-white">
                    {team}
                  </option>
                ))}
              </select>
              {matchOptions.length > 0 && (
                <select
                  value={matchFilter}
                  onChange={(e) => {
                    setMatchFilter(e.target.value);
                    setTeamFilter('');
                  }}
                  className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="" className="bg-slate-700 text-white">All Matches</option>
                  {matchOptions.map((m) => (
                    <option key={m.id} value={m.id} className="bg-slate-700 text-white">
                      {m.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div
            className={`grid items-center gap-2 mb-2 text-sm text-white border-b border-slate-600 pb-1.5 ${
              useAddButton ? 'grid-cols-[1fr_auto_4rem]' : 'grid-cols-[1fr_auto]'
            }`}
          >
            <span
              role="button"
              tabIndex={0}
              onClick={() => toggleSort('name')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleSort('name');
                }
              }}
              className={`cursor-pointer hover:text-slate-200 hover:underline text-left ${sortBy === 'name' ? 'text-slate-200' : ''}`}
            >
              Name{sortBy === 'name' ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : ''}
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={() => toggleSort('credits')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleSort('credits');
                }
              }}
              className={`cursor-pointer hover:text-slate-200 hover:underline text-right ${sortBy === 'credits' ? 'text-slate-200' : ''}`}
            >
              Credits{sortBy === 'credits' ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : ''}
            </span>
            {useAddButton && (
              <span className="text-center text-slate-500 text-xs font-medium uppercase tracking-wide" aria-hidden>
                Act
              </span>
            )}
          </div>

          <div
            className="flex-1 min-h-0 overflow-y-auto overscroll-behavior-contain space-y-2 pr-2"
            style={{
              paddingBottom: 'max(5rem, calc(env(safe-area-inset-bottom, 0px) + 4.5rem))',
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
              overscrollBehavior: 'contain'
            }}
          >
            {filteredAndSorted.length === 0 ? (
              <p className="text-slate-400 py-4">No players match the filters.</p>
            ) : (
              filteredAndSorted.map((player: any, index: number) => {
                const tid = Number(player.tournament_player_id ?? player.id);
                const inTeam = stagedIdSet.has(tid);
                const selectable = canSelect ? canSelect(player) : true;
                const subtext = getRowSubtext ? getRowSubtext(player) : null;
                const rowBase = `p-2.5 sm:p-3 rounded-lg transition-colors border ${
                  useAddButton
                    ? `${inTeam ? 'bg-blue-900/30 border-blue-500/50' : 'bg-slate-700/50 border-slate-600'} ${!selectable && !inTeam ? 'opacity-60' : ''}`
                    : !selectable
                      ? 'bg-slate-700/60 border-slate-600 cursor-not-allowed opacity-60'
                      : 'bg-slate-700/50 border-slate-600 hover:bg-slate-600/60 cursor-pointer'
                }`;
                const nameBlock = (
                  <div className="flex items-center space-x-3 min-w-0">
                    <span className="text-xl shrink-0">{getRoleIcon(player.role)}</span>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-100 flex items-center gap-1 min-w-0">
                        {player.name || player.player_name}
                        {isForeign(player) && <ForeignIcon />}
                        {inTeam && <span className="text-[10px] sm:text-xs font-semibold text-blue-300">Picked</span>}
                      </div>
                      <div className="text-sm text-slate-300">{player.team || player.team_name}</div>
                      {subtext && <div className="text-xs mt-0.5">{subtext}</div>}
                    </div>
                  </div>
                );
                const creditsBlock = (
                  <div className={`text-right ${useAddButton ? 'shrink-0' : ''}`}>
                    <div className="font-semibold text-slate-100">{player.credits ?? 9} cr</div>
                  </div>
                );
                return (
                  <div
                    key={player.tournament_player_id ?? player.id ?? `player-${index}`}
                    onClick={!useAddButton ? () => selectable && onSelect(player) : undefined}
                    onKeyDown={
                      !useAddButton
                        ? (e) => {
                            if ((e.key === 'Enter' || e.key === ' ') && selectable) {
                              e.preventDefault();
                              onSelect(player);
                            }
                          }
                        : undefined
                    }
                    role={!useAddButton ? 'button' : undefined}
                    tabIndex={!useAddButton && selectable ? 0 : undefined}
                    className={rowBase}
                  >
                    {useAddButton ? (
                      <div className="grid grid-cols-[1fr_auto_4rem] gap-2 items-center">
                        {nameBlock}
                        {creditsBlock}
                        <button
                          type="button"
                          disabled={inTeam ? !onRemoveStaged : !selectable}
                          onClick={() => {
                            if (inTeam) {
                              if (onRemoveStaged) onRemoveStaged(tid);
                              return;
                            }
                            if (selectable) onSelect(player);
                          }}
                          className={`shrink-0 justify-self-center rounded-lg px-3 py-2 text-lg font-bold leading-none transition-colors ${
                            inTeam
                              ? (onRemoveStaged
                                  ? 'bg-red-600 text-white hover:bg-red-500'
                                  : 'bg-slate-600 text-slate-400 cursor-not-allowed')
                              : selectable
                              ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                              : 'bg-slate-600 text-slate-400 cursor-not-allowed'
                          }`}
                          aria-label={`${inTeam ? 'Remove' : 'Add'} ${player.name || player.player_name || 'player'}`}
                        >
                          {inTeam ? '−' : '+'}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        {nameBlock}
                        {creditsBlock}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default PlayerPickerModal;
